/**
 * InvoicePdfActions — UI component providing Generate / View / Download
 * buttons for invoice PDF generation within the Billing tab's Invoice Documents table.
 *
 * Designed for use in a TanStack table cell — renders compact icon buttons.
 */

import { useState, useCallback } from 'react';
import { FileText, Download, Eye, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { InvoicePdfTransaction } from '@/types/invoice-pdf';
import {
  generateAndStoreInvoicePdf,
  getStoredInvoicePdf,
  hasStoredPdf,
  openPdfInNewTab,
  downloadPdf,
} from '@/services/invoice-pdf-service';

interface InvoicePdfActionsProps {
  /** Invoice data mapped to InvoicePdfTransaction shape */
  invoice: InvoicePdfTransaction;
}

export function InvoicePdfActions({ invoice }: InvoicePdfActionsProps) {
  const invoiceKey = invoice.invoiceNumber;
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => hasStoredPdf(invoiceKey));

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      await generateAndStoreInvoicePdf(invoice);
      setHasGenerated(true);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [invoice]);

  const handleView = useCallback(async () => {
    const stored = await getStoredInvoicePdf(invoiceKey);
    if (stored) {
      openPdfInNewTab(stored.pdfBlob);
    }
  }, [invoiceKey]);

  const handleDownload = useCallback(async () => {
    const stored = await getStoredInvoicePdf(invoiceKey);
    if (stored) {
      downloadPdf(stored.pdfBlob, `${invoice.invoiceNumber}.pdf`);
    }
  }, [invoiceKey, invoice.invoiceNumber]);

  // Loading state
  if (isGenerating) {
    return (
      <Button variant="ghost" size="sm" disabled className="h-7 w-7 p-0">
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  // Generated state — show badge + view/download actions
  if (hasGenerated) {
    return (
      <div className="flex items-center gap-1">
        <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs px-1.5 py-0">
          <CheckCircle2 className="h-3 w-3 mr-0.5" />
          PDF
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handleView}
          title="View PDF"
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handleDownload}
          title="Download PDF"
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  // Default state — generate button
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-7 p-0"
      onClick={handleGenerate}
      title="Generate PDF"
    >
      <FileText className="h-4 w-4" />
    </Button>
  );
}
